import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { Col, Menu, Row } from "antd";
import { allRoutes, routeObjectType } from "../../routes/routesController";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import config from "../../config/config";
import { checkUserHasRole } from "../../utils/auth-utils";
import { LogoutOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";
import { DatePicker } from "../../components/ui/datepicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import { stat } from "fs";
import {
  saveUserAppointmentTimeSlotPreference,
  getUserSettings,
} from "../../services/userService";
import { setSettings } from "../../redux/reduxSettings/settingSlice";
import { setAppointmentsData } from "../../redux/reduxAppointments/appointmentsSlice";
import { getAppointments } from "../../pages/networkCalls";
import UserSelectedTimeFrame from "./UserSelectedTimeFrame/UserSelectedTimeFrame";
import { SelectConsultingType } from "./SelectConsultingType";
import { formatAppointments } from "../../pages/StaffHome/utils";

dayjs.extend(utc);
dayjs.extend(timezone);

export function TopBar() {
  const settingsStartDateTime = useAppSelector(
    (state) => state.settings.kanbanStartDateTime
  );
  const settingsConsultingSlot = useAppSelector(
    (state) => state.settings.consultingSlot
  );
  const settingsTimezone = useAppSelector((state) => state.settings.timezone);
  const dispatch = useAppDispatch();
  const subject = useAppSelector((state) => state.auth.subject);
  const location = useLocation();
  const [currentSelectedKey, setCurrentSelectedKey] = useState("addpatient");

  // Sync currentSelectedKey with the current route
  useEffect(() => {
    // Find the route whose path matches the current location.pathname
    const match = allRoutes.find((route) =>
      location.pathname.startsWith(route.path)
    );
    if (match && match.key !== currentSelectedKey) {
      setCurrentSelectedKey(match.key);
    }
  }, [location.pathname]);

  // Local state for date and consulting type
  const [selectedDate, setSelectedDate] = useState(() => {
    if (settingsStartDateTime && settingsTimezone) {
      return dayjs.tz(settingsStartDateTime, settingsTimezone);
    } else if (settingsStartDateTime) {
      return dayjs(settingsStartDateTime);
    } else {
      return dayjs();
    }
  });
  const [consultingSlot, setConsultingSlot] = useState<string>("");
  // Keep consultingSlot in sync with Redux if it changes externally
  useEffect(() => {
    if (settingsConsultingSlot) {
      // Ensure value is lowercase to match SelectConsultingType options
      setConsultingSlot(settingsConsultingSlot.toLowerCase());
    }
  }, [settingsConsultingSlot]);

  const navigate = useNavigate();
  const { roles, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out");
    } catch (e) {
      // Session is cleared client-side regardless; surface nothing noisy.
    }
  };

  const onSelect = (e: { key: React.SetStateAction<string> }) => {
    var itemPath = allRoutes.filter((item) => item.key === e.key)[0].path;
    var itemKey = e.key;

    if (itemPath == null || itemPath === undefined) {
      itemPath = "/addpatient";
      itemKey = "addpatient";
    }
    setCurrentSelectedKey(itemKey);
    navigate(itemPath);
  };
  useEffect(() => {
    // navigate("/addpatient");
  }, []);
  function showValidRoutes(routes: routeObjectType[]) {
    return routes

      .filter(
        (route) =>
          route.topbar &&
          checkUserHasRole(route.roles, roles ?? [])
      )
      .map((route) => {
        return {
          path: route.path,
          element: route.element,
          label: route.label,
          key: route.key,
          icon: route.icon,
        };
      });
  }

  // Call API whenever selectedDate or consultingSlot changes
  React.useEffect(() => {
    if (!selectedDate || !consultingSlot || !subject) return;
    const userTz = dayjs.tz.guess();
    const start = selectedDate
      .tz(userTz)
      .hour(6)
      .minute(0)
      .second(0)
      .millisecond(0);
    const end = selectedDate
      .add(1, "day")
      .tz(userTz)
      .hour(2)
      .minute(0)
      .second(0)
      .millisecond(0);

    const startUtc = start.utc().toISOString();
    const endUtc = end.utc().toISOString();
    const timezoneString = userTz;
    const userId = subject;
    saveUserAppointmentTimeSlotPreference(userId, {
      startDateTime: startUtc,
      endDateTime: endUtc,
      consultingSlot: consultingSlot,
      timezone: timezoneString,
    })
      .then(async () => {
        // After saving, fetch latest user settings and update redux
        try {
          const userSettings = await getUserSettings(userId);
          dispatch(
            setSettings({
              kanbanStartDateTime: userSettings.startDateTime,
              kanbanEndDateTime: userSettings.endDateTime,
              consultingSlot: userSettings.consultingSlot,
              timezone: userSettings.timezone,
            })
          );
          // Fetch latest appointments and update redux
          const appointmentResponseObject = await getAppointments();
          let appointments = appointmentResponseObject["data"];
          if (!Array.isArray(appointments)) {
            appointments = [];
          }
          dispatch(setAppointmentsData(formatAppointments(appointments)));
          toast.success(
            "Updated and retrieved user settings and appointments successfully"
          );
        } catch (err) {
          toast.error("Failed to fetch updated user settings or appointments");
        }
      })
      .catch((error) => {
        console.error(
          "Failed to save user appointment time slot preference",
          error
        );
        toast.error(
          error?.message ||
            "Failed to save user appointment time slot preference"
        );
      });
  }, [selectedDate, consultingSlot]);

  // Handler for DatePicker
  function handleDateChange(date: dayjs.Dayjs) {
    setSelectedDate(date);
  }

  return (
    <Row style={{ display: "flex", alignItems: "center" }}>
      <Col className="flex-auto my-0">
        <Menu
          theme="dark"
          onSelect={onSelect}
          mode={"horizontal"}
          selectedKeys={[currentSelectedKey]}
          items={showValidRoutes(allRoutes)}
        ></Menu>
      </Col>
      <Col className="mx-4">
        <UserSelectedTimeFrame />
      </Col>
      <Col className="mx-4">
        <div className="flex items-center gap-2">
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            className="bg-[#23272f] text-white border border-gray-700 rounded-lg shadow-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-150"
          />
          <div className="min-w-[160px]">
            <SelectConsultingType
              value={consultingSlot}
              onChange={setConsultingSlot}
            />
          </div>
        </div>
      </Col>
      <Col className="mx-4">
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={{ color: "white" }}
          title="Log out"
        >
          Logout
        </Button>
      </Col>
    </Row>
  );
}
